import React from 'react'

import type { EmployeeUpdatesDataCollectionQuery } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { FACTORIAL_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { statusTranslation } from 'modules/bookkeepers/lib/dynamicTranslations'
import {
  INCIDENCE_STATUSES,
  INHOUSE_INCIDENCE_STATUSES,
  inhouseStatusCopies,
  professionalsStatusCopies,
} from 'modules/payroll_bundle/lib/Incidences/Status'

export interface FilterState {
  status?: string[]
  search?: string
  type?: string[]
  legalEntitiesIds?: string[]
  startsOn?: string
  endsOn?: string
  containsMessage?: boolean
}

export interface PageInfo {
  endCursor?: string | null
  hasNextPage?: boolean
}

export interface ConnectionWithPageInfo<T> {
  totalCount?: number | null
  nodes: T[]
  pageInfo?: PageInfo
}

export type IncidenceItem = NonNullable<
  EmployeeUpdatesDataCollectionQuery['bookkeepersManagement']
>['incidencesConnection']['nodes'][0]

const isPageInfo = (value: unknown): value is PageInfo => {
  if (!value || typeof value !== 'object') return false
  return 'endCursor' in value || 'hasNextPage' in value
}

export const getConnectionPageInfo = <T>(
  connection:
    | {
        totalCount?: number | null
        nodes: T[]
      }
    | null
    | undefined
): { totalCount: number; endCursor: string; hasNextPage: boolean } => {
  const rawPageInfo = connection && 'pageInfo' in connection ? connection.pageInfo : undefined
  const pageInfo = rawPageInfo && isPageInfo(rawPageInfo) ? rawPageInfo : undefined

  return {
    totalCount: connection?.totalCount ?? 0,
    endCursor: pageInfo?.endCursor ?? '0',
    hasNextPage: pageInfo?.hasNextPage ?? false,
  }
}

export interface DataCollectionState {
  filters?: {
    status?: string[]
    type?: string[]
    legalEntityIds?: string[]
    workplaceIds?: string[]
    effectiveDate?: {
      from?: Date
      to?: Date
    }
    hasMessage?: boolean[]
  }
  search?: string
  sortings?: Array<{
    field?: string
    order?: string
  }>
}

export type PresetStatusKey =
  | typeof INCIDENCE_STATUSES.in_preparation
  | typeof INCIDENCE_STATUSES.to_do
  | typeof INCIDENCE_STATUSES.doing
  | typeof INCIDENCE_STATUSES.done
  | typeof INCIDENCE_STATUSES.discarded

export interface DynamicPreset {
  label: string
  filter: {
    status: PresetStatusKey[]
  }
  itemsCount: () => number
}

interface StatusCountsData {
  bookkeepersManagement?: {
    inPreparation?: { totalCount?: number | null } | null
    toDo?: { totalCount?: number | null } | null
    doing?: { totalCount?: number | null } | null
    done?: { totalCount?: number | null } | null
    discarded?: { totalCount?: number | null } | null
    reopened?: { totalCount?: number | null } | null
    open?: { totalCount?: number | null } | null
  }
}

export const getStatusCount = (status: string, statusCountsData: StatusCountsData): number => {
  if (!statusCountsData?.bookkeepersManagement) return 0

  const statusMap: Record<string, number> = {
    [INCIDENCE_STATUSES.in_preparation]:
      statusCountsData.bookkeepersManagement.inPreparation?.totalCount ?? 0,
    [INCIDENCE_STATUSES.to_do]: statusCountsData.bookkeepersManagement.toDo?.totalCount ?? 0,
    [INCIDENCE_STATUSES.doing]: statusCountsData.bookkeepersManagement.doing?.totalCount ?? 0,
    [INCIDENCE_STATUSES.done]: statusCountsData.bookkeepersManagement.done?.totalCount ?? 0,
    [INCIDENCE_STATUSES.discarded]:
      statusCountsData.bookkeepersManagement.discarded?.totalCount ?? 0,
    reopened: statusCountsData.bookkeepersManagement.reopened?.totalCount ?? 0,
    open: statusCountsData.bookkeepersManagement.open?.totalCount ?? 0,
  }

  return statusMap[status] ?? 0
}

const isValidPresetStatusKey = (status: string): status is PresetStatusKey => {
  const validStatuses = Object.values(INCIDENCE_STATUSES)
  return validStatuses.some((validStatus) => validStatus === status)
}

const mapStatusToPresetFormat = (status: string): PresetStatusKey => {
  const normalizedStatus = status.replace(/_/g, '-')

  if (isValidPresetStatusKey(normalizedStatus)) {
    return normalizedStatus
  }
  return INCIDENCE_STATUSES.to_do
}

export const createDynamicPresets = (
  statusCountsData: StatusCountsData,
  getStatusCountCallback: (statusKey: string) => number,
  hasInhousePayroll = false
): DynamicPreset[] => {
  const statusOrder = hasInhousePayroll
    ? [
        INHOUSE_INCIDENCE_STATUSES.to_do,
        INHOUSE_INCIDENCE_STATUSES.doing,
        INHOUSE_INCIDENCE_STATUSES.done,
        INHOUSE_INCIDENCE_STATUSES.discarded,
      ]
    : [
        INCIDENCE_STATUSES.in_preparation,
        INCIDENCE_STATUSES.to_do,
        INCIDENCE_STATUSES.doing,
        INCIDENCE_STATUSES.done,
        INCIDENCE_STATUSES.discarded,
      ]

  const labels = hasInhousePayroll ? inhouseStatusCopies() : professionalsStatusCopies()

  return statusOrder.map((statusItem) => {
    const mappedStatus = mapStatusToPresetFormat(statusItem)
    const statusArray = [mappedStatus]
    const labelsRecord: Record<string, string | undefined> = labels
    const label =
      labelsRecord[mappedStatus] ?? statusTranslation(statusItem, false, hasInhousePayroll)

    return {
      label,
      filter: {
        status: statusArray,
      },
      itemsCount: () => getStatusCountCallback(statusItem),
    }
  })
}

export const createStateChangeHandler = (
  setCurrentFilters: React.Dispatch<React.SetStateAction<FilterState>>
) => {
  let previousFilters: FilterState = {}

  return (state: DataCollectionState) => {
    const newFilters: FilterState = {}
    if (state.filters?.status) newFilters.status = state.filters.status
    if (state.search) newFilters.search = state.search
    if (state.filters?.type) newFilters.type = state.filters.type
    if (state.filters?.legalEntityIds) newFilters.legalEntitiesIds = state.filters.legalEntityIds
    if (state.filters?.effectiveDate) {
      if (state.filters.effectiveDate.from)
        newFilters.startsOn = state.filters.effectiveDate.from.toISOString().split('T')[0]
      if (state.filters.effectiveDate.to)
        newFilters.endsOn = state.filters.effectiveDate.to.toISOString().split('T')[0]
    }
    if (state.filters?.hasMessage) newFilters.containsMessage = state.filters.hasMessage[0]

    const filterKeys: Array<keyof FilterState> = [
      'status',
      'search',
      'type',
      'legalEntitiesIds',
      'startsOn',
      'endsOn',
      'containsMessage',
    ]
    const addedFilters = filterKeys.filter((key) => {
      const newValue = newFilters[key]
      const prevValue = previousFilters[key]

      return (
        newValue !== undefined &&
        newValue !== null &&
        (prevValue === undefined ||
          prevValue === null ||
          (Array.isArray(prevValue) && prevValue.length === 0) ||
          (typeof prevValue === 'string' && prevValue === ''))
      )
    })

    // Special handling for effectiveDate: combine startsOn and endsOn into one event
    const hasStartsOn = addedFilters.includes('startsOn')
    const hasEndsOn = addedFilters.includes('endsOn')
    const effectiveDateFilters: Array<keyof FilterState | 'effectiveDate'> = addedFilters.filter(
      (key) => key !== 'startsOn' && key !== 'endsOn'
    )

    if (hasStartsOn || hasEndsOn) {
      effectiveDateFilters.push('effectiveDate')
    }

    setCurrentFilters(newFilters)

    const filterNameMap: Record<string, string> = {
      containsMessage: 'messages',
      legalEntitiesIds: 'legal_entities',
      effectiveDate: 'effective_date',
    }

    const getFilterName = (technicalName: string): string => {
      return filterNameMap[technicalName] || technicalName
    }

    effectiveDateFilters.forEach((filterName) => {
      const displayName = getFilterName(filterName)
      track(trackingEvents.compensations.applyEmployeeUpdateFilter, {
        application: FACTORIAL_APP,
        filter_name: displayName,
      })
    })

    previousFilters = { ...newFilters }
  }
}
